import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/* ─────────────────────── URL Helpers ─────────────────────── */

/**
 * Strip paste artifacts from a stored URL: wrapping quotes, stray control/invisible characters,
 * and surrounding whitespace.
 *
 * Pasting a link out of a spreadsheet cell drags the cell's wrapping quotes along, so rows exist
 * whose stored value is literally `"https://www.youtube.com/watch?v=..."`. That value breaks every
 * consumer downstream — `new URL()` throws on it, so YouTube-ID extraction returns null and the
 * module falls through to the video.js branch, where video.js resolves the quoted string as a
 * RELATIVE path against the site origin and produces a redirect loop.
 *
 * The backend now rejects/cleans these on write, but this runs on READ so the rows already in the
 * database render correctly without a data migration.
 */
export function sanitizeUrlValue(url: string | null | undefined): string | null {
  if (!url) return null;
  const cleaned = url
    // C0 controls (\r \n \t and friends), DEL/C1 controls, the
    // UTF-8 BOM some exporters prepend, and a zero-width space. Written as escapes rather
    // than pasted literally so this source file cannot itself carry invisible characters.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F-\u009F\uFEFF\u200B]/g, "")
    .trim()
    // Wrapping quotes only — an unbalanced or interior quote is left alone so a genuinely
    // malformed value still fails loudly rather than being silently reshaped into something
    // that looks valid.
    .replace(/^(["'])([\s\S]*)\1$/, "$2")
    .trim();
  return cleaned || null;
}

/* ─────────────────────── Bidirectional Text ─────────────────────── */

/**
 * Props to spread onto any element that renders user-authored prose which may
 * mix Arabic and English (messages, notes, announcements, and the inputs used
 * to write them).
 *
 * The app's shell is fixed LTR, so a mixed-script string is laid out as a
 * single left-to-right paragraph. The Arabic run still reads right-to-left
 * inside it, which throws trailing punctuation, digits and Latin words to the
 * wrong end — the line renders scrambled even though the stored text is intact.
 *
 * `dir="auto"` derives the base direction from the first strong character
 * instead of the container, `unicode-bidi: plaintext` re-applies that heuristic
 * per line so each line of a multi-line message gets its own direction, and
 * `text-align: start` then aligns each line by its own direction (Arabic right,
 * English left) rather than by the container's.
 */
export const bidiText = {
  dir: "auto",
  style: { unicodeBidi: "plaintext", textAlign: "start" },
} as const;

/* ─────────────────────── Formatting Helpers ─────────────────────── */

/**
 * Convert a total number of seconds into a human-readable "Xh Ym" or "Xm" string.
 * These helpers were previously duplicated across dashboard.tsx, training.tsx, etc.
 */
export function formatSeconds(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds <= 0) return "0m";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Alias kept for backwards compatibility with existing dashboard code. */
export const formatMinutes = formatSeconds;

/** Capitalise the first letter of a string. */
export function capitalize(s: string): string {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Format a UTC ISO date string to the local locale date string. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

/** Format a UTC ISO date string to a locale-aware date + time string. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

/**
 * Human-readable content length for a course, track, or module.
 *
 * Courses and tracks report `total_duration_seconds`, derived server-side from their modules'
 * durations, so the value can be any number of seconds rather than a round number of hours.
 * Returns an em dash for a course with no content yet — "0m" reads like a bug.
 */
export function formatContentDuration(totalSeconds: number | null | undefined): string {
  if (!totalSeconds || totalSeconds <= 0) return "—";

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.round((totalSeconds % 3600) / 60);

  // A 59m40s course rounds to 60 minutes, which must read as "1h", not "0h 60m".
  if (minutes === 60) return `${hours + 1}h`;
  if (hours === 0) return minutes === 0 ? "<1m" : `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

/* ─────────────────────── Progress Helpers ─────────────────────── */

/**
 * Compute how many seconds of a module a student has effectively watched,
 * given their raw active_watch_seconds and the module duration.
 */
export function computeWatchPercent(
  activeWatchSeconds: number | undefined | null,
  durationSeconds: number,
): number {
  if (!activeWatchSeconds || !durationSeconds) return 0;
  return Math.min(100, Math.round((activeWatchSeconds / durationSeconds) * 100));
}

/** Play a high-quality, crisp notification chime using Web Audio API (cross-browser and zero-network dependencies) */
export function playNotificationSound() {
  try {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();

    // First tone (chime note D5)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(587.33, ctx.currentTime);
    gain1.gain.setValueAtTime(0, ctx.currentTime);
    gain1.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 0.04);
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc1.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.3);

    // Second tone (harmonic higher note A5, slightly delayed)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(880, ctx.currentTime + 0.08);
    gain2.gain.setValueAtTime(0, ctx.currentTime + 0.08);
    gain2.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 0.12);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc2.start(ctx.currentTime + 0.08);
    osc2.stop(ctx.currentTime + 0.4);
  } catch (e) {
    console.warn("Audio Context playback failed or blocked:", e);
  }
}
